import React, { useState, useEffect, useContext } from 'react';
import AdminNavBar from "@/components/AdminNavBar";
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { AuthContext } from '@/App';
import { Send, ArrowLeft, Upload, X, Mail, Users, Bold, Italic, List, ListOrdered } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSendEmail() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCompId = searchParams.get('competition_id');

  const [loading, setLoading] = useState(false);
  const [competitions, setCompetitions] = useState([]);
  const [selectedRecipientType, setSelectedRecipientType] = useState('all'); // 'all' or 'competition'
  const [selectedCompetition, setSelectedCompetition] = useState(preselectedCompId || '');
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [subject, setSubject] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  // TipTap editor with proper configuration
  const editor = useEditor({
    extensions: [StarterKit, TextStyle, Color],
    content: '<p>Write your message here...</p>',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px] p-4 [&_strong]:font-bold [&_em]:italic [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:my-1',
      },
    },
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadCompetitions();
  }, [user, navigate]);

  useEffect(() => {
    if (selectedRecipientType === 'all') {
      loadAllPlayers();
    } else if (selectedCompetition) {
      loadCompetitionPlayers();
    }
  }, [selectedRecipientType, selectedCompetition]);

  useEffect(() => {
    if (selectAll) {
      setSelectedPlayers(players.map(p => p.email));
    } else {
      setSelectedPlayers([]);
    }
  }, [selectAll, players]);

  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/admin/competitions`);
      setCompetitions(response.data);
      if (preselectedCompId) {
        setSelectedRecipientType('competition');
        setSelectedCompetition(preselectedCompId);
      }
    } catch (error) {
      console.error('Error loading competitions:', error);
      toast.error('Failed to load competitions');
    }
  };

  const loadAllPlayers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/all-players-for-email`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Error loading players:', error);
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const loadCompetitionPlayers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/competitions/${selectedCompetition}/players-for-email`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Error loading players:', error);
      toast.error('Failed to load competition players');
    } finally {
      setLoading(false);
    }
  };

  const togglePlayer = (email) => {
    setSelectedPlayers(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Check total size
    const currentSize = attachments.reduce((sum, att) => sum + att.file.size, 0);
    const newSize = files.reduce((sum, file) => sum + file.size, 0);
    const totalSize = currentSize + newSize;
    
    if (totalSize > 10 * 1024 * 1024) {
      toast.error('Total attachment size cannot exceed 10MB');
      return;
    }
    
    if (attachments.length + files.length > 5) {
      toast.error('Maximum 5 attachments allowed');
      return;
    }
    
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream'
    }));
    
    setAttachments([...attachments, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const icons = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      txt: '📃',
      zip: '🗜️'
    };
    return icons[ext] || '📎';
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    
    if (!editor || !editor.getHTML() || editor.getHTML() === '<p></p>') {
      toast.error('Please enter a message');
      return;
    }
    
    if (selectedPlayers.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('html_content', editor.getHTML());
      formData.append('text_content', editor.getText());
      
      // Backend expects comma-separated string in 'recipient_emails'
      formData.append('recipient_emails', selectedPlayers.join(','));
      
      attachments.forEach(att => {
        formData.append('files', att.file);
      });
      
      const response = await axios.post(
        `${API}/admin/send-email`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`Email sent successfully to ${selectedPlayers.length} recipient(s)!`);
      
      // Reset form
      setSubject('');
      editor?.commands.setContent('<p>Write your message here...</p>');
      setAttachments([]);
      setSelectedPlayers([]);
      setSelectAll(false);
      
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-app">
      <AdminNavBar />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-accent hover:text-ink mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-accent-tint w-12 h-12 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-ink tracking-tight">Email Players</h1>
              <p className="text-ink-muted">Send emails with rich formatting and attachments</p>
            </div>
          </div>
        </div>

        {/* Email Form */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <form onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
            <div className="space-y-4">
              {/* Recipient Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Send To
                </label>
                <select
                  value={selectedRecipientType}
                  onChange={(e) => {
                    setSelectedRecipientType(e.target.value);
                    setSelectedPlayers([]);
                    setSelectAll(false);
                  }}
                  className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  <option value="all">All Registered Players</option>
                  <option value="competition">Competition Players</option>
                </select>
              </div>

              {/* Competition Selector */}
              {selectedRecipientType === 'competition' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-ink-muted mb-2">
                    Select Competition
                  </label>
                  <select
                    value={selectedCompetition}
                    onChange={(e) => setSelectedCompetition(e.target.value)}
                    className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    <option value="">Choose competition...</option>
                    {competitions.map(comp => (
                      <option key={comp.id} value={comp.id}>{comp.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Player List */}
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
                </div>
              ) : (
                <>
                  {players.length > 0 && (
                    <>
                      <div className="mb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={(e) => setSelectAll(e.target.checked)}
                            className="rounded border-line-strong text-accent focus:ring-accent"
                          />
                          <span className="text-sm font-medium text-ink-muted">Select All ({players.length})</span>
                        </label>
                      </div>
                      <div className="max-h-96 overflow-y-auto border border-line rounded-lg p-2 space-y-1">
                        {players.map(player => (
                          <label
                            key={player.email}
                            className="flex items-center gap-2 p-2 hover:bg-app rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPlayers.includes(player.email)}
                              onChange={() => togglePlayer(player.email)}
                              className="rounded border-line-strong text-accent focus:ring-accent"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-ink truncate">{player.name}</p>
                              <p className="text-xs text-ink-soft truncate">{player.email}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                      <p className="text-sm text-ink-muted mt-2">
                        {selectedPlayers.length} selected
                      </p>
                    </>
                  )}
                </>
              )}

              {/* Subject */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject"
                  className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  required
                />
              </div>

              {/* Message with improved TipTap editor */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Message *
                </label>
                {/* Toolbar with better visual feedback */}
                <div className="border border-line-strong rounded-t-lg bg-app p-2 flex gap-1 flex-wrap">
                  <button
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    className={`p-2 rounded-lg font-bold transition-all border-2 ${
                      editor?.isActive('bold') 
                        ? 'bg-ink text-white border-ink shadow-md scale-105' 
                        : 'bg-white text-ink-muted border-line-strong hover:bg-surface-2 hover:border-line-strong'
                    }`}
                    type="button"
                    title="Bold"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded-lg italic transition-all border-2 ${
                      editor?.isActive('italic') 
                        ? 'bg-ink text-white border-ink shadow-md scale-105' 
                        : 'bg-white text-ink-muted border-line-strong hover:bg-surface-2 hover:border-line-strong'
                    }`}
                    type="button"
                    title="Italic"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <div className="w-px bg-line-strong mx-1"></div>
                  <button
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded-lg transition-all border-2 ${
                      editor?.isActive('bulletList') 
                        ? 'bg-ink text-white border-ink shadow-md scale-105' 
                        : 'bg-white text-ink-muted border-line-strong hover:bg-surface-2 hover:border-line-strong'
                    }`}
                    type="button"
                    title="Bullet List"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded-lg transition-all border-2 ${
                      editor?.isActive('orderedList') 
                        ? 'bg-ink text-white border-ink shadow-md scale-105' 
                        : 'bg-white text-ink-muted border-line-strong hover:bg-surface-2 hover:border-line-strong'
                    }`}
                    type="button"
                    title="Numbered List"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </button>
                </div>
                {/* Editor with proper styling */}
                <div className="border border-t-0 border-line-strong rounded-b-lg bg-white">
                  <EditorContent editor={editor} />
                </div>
                <p className="text-xs text-ink-soft mt-1">
                  Tip: Bold and italic formatting will be highlighted when active. Lists will indent properly.
                </p>
              </div>

              {/* Attachments */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Attachments (Max 10MB total, 5 files max)
                </label>
                <div className="flex items-center gap-2">
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-line-strong rounded-lg p-4 text-center hover:border-accent transition-colors">
                      <Upload className="w-6 h-6 mx-auto text-ink-soft mb-2" />
                      <p className="text-sm text-ink-muted">Click to upload files</p>
                      <p className="text-xs text-ink-soft mt-1">PDF, DOC, XLS, Images, etc.</p>
                    </div>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip"
                    />
                  </label>
                </div>
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((att, index) => (
                      <div key={index} className="flex items-center justify-between bg-app p-2 rounded-lg">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-xl">{getFileIcon(att.name)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-ink truncate">{att.name}</p>
                            <p className="text-xs text-ink-soft">{formatFileSize(att.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="p-1 hover:bg-red-100 rounded text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-ink-soft">
                      Total: {formatFileSize(attachments.reduce((sum, att) => sum + att.size, 0))}
                    </p>
                  </div>
                )}
              </div>

              {/* Send Button */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-3 border border-line-strong rounded-lg font-semibold text-ink-muted hover:bg-app transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending || selectedPlayers.length === 0}
                  className="flex-1 px-6 py-3 bg-ink text-white rounded-lg font-semibold hover:bg-ink-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Email to {selectedPlayers.length} {selectedPlayers.length === 1 ? 'Player' : 'Players'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
